import { Localized, useLocalization } from "@fluent/react";
import BlurOnIcon from "@mui/icons-material/BlurOn";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DrawIcon from "@mui/icons-material/Draw";
import MouseIcon from "@mui/icons-material/Mouse";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import UndoIcon from "@mui/icons-material/Undo";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
} from "@mui/material";
import { Canvas, FabricImage, IText, PencilBrush, Rect } from "fabric";
import { useCallback, useEffect, useRef, useState } from "react";

type EditorTool = "select" | "draw";

type ScreenshotEditorDialogProps = {
    open: boolean;
    screenshot: Blob;
    onCancel: () => void;
    onApply: (screenshot: Blob) => void;
};

const MARKUP_COLOR = "#d32f2f";

export const ScreenshotEditorDialog = ({
    open,
    screenshot,
    onCancel,
    onApply,
}: ScreenshotEditorDialogProps) => {
    const { l10n } = useLocalization();
    const canvasElementRef = useRef<HTMLCanvasElement | null>(null);
    const dialogContentRef = useRef<HTMLDivElement | null>(null);
    const fabricCanvasRef = useRef<Canvas | null>(null);
    const annotationHistoryRef = useRef<any[]>([]);
    const exportMultiplierRef = useRef(1);
    const [tool, setTool] = useState<EditorTool>("select");
    const [ready, setReady] = useState(false);
    const [saving, setSaving] = useState(false);
    const [historyLength, setHistoryLength] = useState(0);
    const [dialogEntered, setDialogEntered] = useState(false);
    const [loadError, setLoadError] = useState(false);

    const setCanvasTool = useCallback((nextTool: EditorTool) => {
        setTool(nextTool);
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;

        canvas.isDrawingMode = nextTool === "draw";
        canvas.selection = nextTool === "select";
        canvas.discardActiveObject();
        canvas.requestRenderAll();
    }, []);

    useEffect(() => {
        if (!open || !dialogEntered || !canvasElementRef.current) return;

        let active = true;
        const screenshotUrl = URL.createObjectURL(screenshot);
        const imageElement = new Image();
        const canvas = new Canvas(canvasElementRef.current, {
            preserveObjectStacking: true,
            selection: true,
        });
        fabricCanvasRef.current = canvas;
        annotationHistoryRef.current = [];
        setHistoryLength(0);
        setReady(false);
        setLoadError(false);
        setTool("select");

        const registerObject = ({ target }: { target?: any }) => {
            if (!target || target === canvas.backgroundImage) return;
            annotationHistoryRef.current.push(target);
            setHistoryLength(annotationHistoryRef.current.length);
        };

        canvas.on("object:added", registerObject);

        imageElement.onload = () => {
            if (!active) return;

            const imageWidth = imageElement.naturalWidth || imageElement.width || 1;
            const imageHeight = imageElement.naturalHeight || imageElement.height || 1;
            const maxEditorWidth = Math.max(320, window.innerWidth - 100);
            const maxEditorHeight = Math.max(240, window.innerHeight - 230);
            const displayScale = Math.min(
                1,
                maxEditorWidth / imageWidth,
                maxEditorHeight / imageHeight
            );
            const canvasWidth = Math.round(imageWidth * displayScale);
            const canvasHeight = Math.round(imageHeight * displayScale);
            const image = new FabricImage(imageElement, {
                left: 0,
                top: 0,
                originX: "left",
                originY: "top",
                scaleX: displayScale,
                scaleY: displayScale,
                selectable: false,
                evented: false,
            });

            canvas.setDimensions({ width: canvasWidth, height: canvasHeight });
            canvas.backgroundImage = image;
            exportMultiplierRef.current = 1 / displayScale;

            const pencilBrush = new PencilBrush(canvas);
            pencilBrush.color = MARKUP_COLOR;
            pencilBrush.width = 4;
            canvas.freeDrawingBrush = pencilBrush;
            canvas.requestRenderAll();
            setReady(true);
        };

        imageElement.onerror = () => {
            if (!active) return;
            setReady(false);
            setLoadError(true);
        };
        imageElement.src = screenshotUrl;

        return () => {
            active = false;
            imageElement.onload = null;
            imageElement.onerror = null;
            URL.revokeObjectURL(screenshotUrl);
            fabricCanvasRef.current = null;
            void canvas.dispose();
        };
    }, [dialogEntered, open, screenshot]);

    const addRectangle = useCallback((redact: boolean) => {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;

        setCanvasTool("select");
        const width = Math.min(240, canvas.getWidth() * 0.4);
        const height = Math.min(100, canvas.getHeight() * 0.2);
        const rectangle = new Rect({
            left: (canvas.getWidth() - width) / 2,
            top: (canvas.getHeight() - height) / 2,
            width,
            height,
            fill: redact ? "#1f1f1f" : "rgba(211, 47, 47, 0.08)",
            stroke: redact ? "#1f1f1f" : MARKUP_COLOR,
            strokeWidth: redact ? 0 : 4,
            cornerColor: MARKUP_COLOR,
            transparentCorners: false,
        });
        canvas.add(rectangle);
        canvas.setActiveObject(rectangle);
        canvas.requestRenderAll();
    }, [setCanvasTool]);

    const addText = useCallback(() => {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;

        setCanvasTool("select");
        const text = new IText(
            l10n.getString("bug-report-editor-text-placeholder", null, "Add a note"),
            {
                left: canvas.getWidth() * 0.2,
                top: canvas.getHeight() * 0.2,
                fill: MARKUP_COLOR,
                fontFamily: "Arial",
                fontSize: 24,
                fontWeight: "bold",
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                cornerColor: MARKUP_COLOR,
                transparentCorners: false,
                hiddenTextareaContainer: dialogContentRef.current,
            }
        );
        canvas.add(text);
        canvas.setActiveObject(text);
        text.enterEditing();
        text.selectAll();
        canvas.requestRenderAll();
    }, [l10n, setCanvasTool]);

    const deleteSelected = useCallback(() => {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;

        const selected = canvas.getActiveObjects();
        selected.forEach((object) => {
            canvas.remove(object);
            annotationHistoryRef.current = annotationHistoryRef.current.filter((entry) => entry !== object);
        });
        setHistoryLength(annotationHistoryRef.current.length);
        canvas.discardActiveObject();
        canvas.requestRenderAll();
    }, []);

    const undo = useCallback(() => {
        const canvas = fabricCanvasRef.current;
        const object = annotationHistoryRef.current.pop();
        if (!canvas || !object) return;

        canvas.remove(object);
        setHistoryLength(annotationHistoryRef.current.length);
        canvas.discardActiveObject();
        canvas.requestRenderAll();
    }, []);

    const apply = useCallback(async () => {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;

        setSaving(true);
        canvas.discardActiveObject();
        canvas.requestRenderAll();
        const editedScreenshot = await canvas.toBlob({
            format: "png",
            multiplier: exportMultiplierRef.current,
        });
        setSaving(false);

        if (editedScreenshot) {
            onApply(editedScreenshot);
        }
    }, [onApply]);

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onCancel}
            fullWidth
            maxWidth={false}
            TransitionProps={{
                onEntered: () => setDialogEntered(true),
                onExited: () => setDialogEntered(false),
            }}
            PaperProps={{
                sx: {
                    width: "96vw",
                    maxWidth: "96vw",
                    height: "94vh",
                    maxHeight: "94vh",
                    m: 1,
                },
            }}
        >
            <DialogTitle>
                <Localized id="bug-report-editor-title">Mark the screenshot</Localized>
            </DialogTitle>
            <DialogContent ref={dialogContentRef} sx={{ display: "flex", minHeight: 0 }}>
                <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <ToggleButtonGroup
                            value={tool}
                            exclusive
                            size="small"
                            onChange={(_, nextTool: EditorTool | null) => nextTool && setCanvasTool(nextTool)}
                        >
                            <ToggleButton value="select" aria-label={l10n.getString("bug-report-editor-select", null, "Select")}>
                                <MouseIcon fontSize="small" />
                            </ToggleButton>
                            <ToggleButton value="draw" aria-label={l10n.getString("bug-report-editor-draw", null, "Draw")}>
                                <DrawIcon fontSize="small" />
                            </ToggleButton>
                        </ToggleButtonGroup>

                        <Tooltip title={l10n.getString("bug-report-editor-rectangle", null, "Rectangle")}>
                            <IconButton size="small" onClick={() => addRectangle(false)} disabled={!ready}>
                                <CropSquareIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={l10n.getString("bug-report-editor-text", null, "Text")}>
                            <IconButton size="small" onClick={addText} disabled={!ready}>
                                <TextFieldsIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={l10n.getString("bug-report-editor-redact", null, "Redact sensitive data")}>
                            <IconButton size="small" onClick={() => addRectangle(true)} disabled={!ready}>
                                <BlurOnIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={l10n.getString("undo", null, "Undo")}>
                            <span>
                                <IconButton size="small" onClick={undo} disabled={!ready || historyLength === 0}>
                                    <UndoIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={l10n.getString("delete", null, "Delete")}>
                            <IconButton size="small" onClick={deleteSelected} disabled={!ready}>
                                <DeleteOutlineIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>

                    <Box
                        sx={{
                            width: "100%",
                            flex: 1,
                            minHeight: 0,
                            overflow: "auto",
                            border: "1px solid",
                            borderColor: "divider",
                            bgcolor: "grey.200",
                            p: 1,
                            position: "relative",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "flex-start",
                        }}
                    >
                        <canvas ref={canvasElementRef} />
                        {!ready && !loadError && (
                            <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ position: "absolute", inset: 0 }}>
                                <CircularProgress />
                                <Localized id="bug-report-editor-loading">Loading screenshot...</Localized>
                            </Stack>
                        )}
                    </Box>
                    {loadError && (
                        <Alert severity="error">
                            <Localized id="bug-report-editor-load-error">
                                The screenshot could not be loaded in the editor.
                            </Localized>
                        </Alert>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} disabled={saving}>
                    <Localized id="cancel">Cancel</Localized>
                </Button>
                <Button variant="contained" onClick={apply} disabled={!ready || saving}>
                    <Localized id="apply">Apply</Localized>
                </Button>
            </DialogActions>
        </Dialog>
    );
};
